import React, { useRef, useEffect, useState } from 'react';
import { Calendar, Download } from 'lucide-react';
import mermaid from 'mermaid';
import svgPanZoom from 'svg-pan-zoom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import CommentSection from './CommentSection';
import BlogIntro from '../common/BlogIntro';
import EditPostForm from './EditPostForm';
import { formatDateLine, getChangelog, isLivingDoc } from '../../utils/journalPosts';
import { prepareBlogHtml } from '../../utils/prepareBlogHtml';

const LatestPostDisplay = ({ post, userId, isAdmin, setMessage, onPostUpdated, isLatestPost, onShowLatestPost, sidebar }) => {
  const contentRef = useRef(null);
  const pdfContentRef = useRef(null);
  const panZoomInstancesRef = useRef([]);
  const resizeCleanupRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Render Mermaid diagrams and init zoom where diagram-box + zoom-controls exist
  useEffect(() => {
    if (!post?.content || !contentRef.current) return;
    const container = contentRef.current;
    const nodes = container.querySelectorAll('.mermaid');
    if (nodes.length === 0) return;
    mermaid.initialize({ startOnLoad: false, theme: 'neutral' });
    (async () => {
      try {
        await mermaid.run({ nodes });
        // Destroy previous zoom instances
        panZoomInstancesRef.current.forEach((inst) => { try { inst.destroy(); } catch (_) {} });
        panZoomInstancesRef.current = [];
        if (resizeCleanupRef.current) { resizeCleanupRef.current(); resizeCleanupRef.current = null; }
        // Init zoom for each .diagram-box that has .mermaid and .zoom-controls
        const diagramBoxes = container.querySelectorAll('.diagram-box');
        diagramBoxes.forEach((box) => {
          const svg = box.querySelector('.mermaid svg');
          const controls = box.querySelector('.zoom-controls');
          const buttons = controls ? Array.from(controls.querySelectorAll('button')) : [];
          if (!svg || buttons.length < 2) return;
          const panZoom = svgPanZoom(svg, {
            zoomEnabled: true,
            controlIconsEnabled: false,
            fit: true,
            center: true,
            minZoom: 0.1,
            maxZoom: 10
          });
          panZoomInstancesRef.current.push(panZoom);
          // First two buttons are always zoom in / zoom out
          buttons[0].onclick = (e) => { e.preventDefault(); panZoom.zoomIn(); };
          buttons[1].onclick = (e) => { e.preventDefault(); panZoom.zoomOut(); };
          // Optional: reset and/or fullscreen (by id or button text)
          const isFullscreenBtn = (b) => b && (b.id === 'btn-fullscreen' || /fullscreen/i.test(b.textContent || ''));
          const isResetBtn = (b) => b && (b.id === 'reset' || /reset/i.test(b.textContent || ''));
          for (let i = 2; i < buttons.length; i++) {
            if (isFullscreenBtn(buttons[i])) {
              buttons[i].onclick = (e) => {
                e.preventDefault();
                if (!document.fullscreenElement) {
                  box.requestFullscreen().catch(() => {});
                } else {
                  document.exitFullscreen();
                }
              };
            } else if (isResetBtn(buttons[i])) {
              buttons[i].onclick = (e) => {
                e.preventDefault();
                panZoom.resetZoom();
                panZoom.center();
                panZoom.fit();
              };
            }
          }
        });
        const instances = panZoomInstancesRef.current;
        const onResize = () => {
          instances.forEach((inst) => { try { inst.resize(); inst.fit(); inst.center(); } catch (_) {} });
        };
        const fullscreenHandler = () => setTimeout(onResize, 100);
        window.addEventListener('resize', onResize);
        document.addEventListener('fullscreenchange', fullscreenHandler);
        resizeCleanupRef.current = () => {
          window.removeEventListener('resize', onResize);
          document.removeEventListener('fullscreenchange', fullscreenHandler);
        };
      } catch (err) {
        console.warn('Mermaid render error:', err);
      }
    })();
    return () => {
      if (resizeCleanupRef.current) { resizeCleanupRef.current(); resizeCleanupRef.current = null; }
      panZoomInstancesRef.current.forEach((inst) => { try { inst.destroy(); } catch (_) {} });
      panZoomInstancesRef.current = [];
    };
  }, [post?.id, post?.content]);

  const handleDownloadPdf = async () => {
    if (!pdfContentRef.current || isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    const el = pdfContentRef.current;
    const originalWidth = el.style.width;
    const originalMaxWidth = el.style.maxWidth;
    try {
      // Wait for fonts so text renders correctly in the canvas
      await document.fonts.ready;
      // Fix width for consistent A4-ish layout (170mm ≈ 640px at 96dpi)
      const captureWidthPx = 640;
      const captureScale = 2;
      el.style.width = `${captureWidthPx}px`;
      el.style.maxWidth = `${captureWidthPx}px`;
      // Let layout settle after width change
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 150)));

      // Compute logical page-break positions from block boundaries (avoid cutting words/elements)
      const contentTop = el.getBoundingClientRect().top;
      const contentHeightPx = el.scrollHeight || el.offsetHeight;
      const blockSelectors = 'p, h1, h2, h3, h4, h5, h6, ul, ol, pre, blockquote, table, .flow-step, .conceptual-block, .schema-container, .db-card, .diagram-box, .arch-flow, section, .pattern-list, .evidence-block, .fail-grid, .evidence-table, .unified-table';
      const breakPointsPx = [0];
      el.querySelectorAll(blockSelectors).forEach((block) => {
        const rect = block.getBoundingClientRect();
        const bottom = rect.bottom - contentTop;
        if (bottom > 2 && bottom < contentHeightPx - 2) breakPointsPx.push(bottom);
      });
      breakPointsPx.push(contentHeightPx);
      breakPointsPx.sort((a, b) => a - b);
      const uniquePx = [0];
      for (let i = 1; i < breakPointsPx.length; i++) {
        if (breakPointsPx[i] - uniquePx[uniquePx.length - 1] >= 3) uniquePx.push(breakPointsPx[i]);
      }
      if (uniquePx[uniquePx.length - 1] !== contentHeightPx) uniquePx.push(contentHeightPx);

      const canvas = await html2canvas(el, {
        scale: captureScale,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
        foreignObjectRendering: false, // more reliable cross-browser than true for SVGs
        onclone: (clonedDoc, clonedEl) => {
          // PDF capture: make diagram boxes show full content (no overflow clip)
          const style = clonedDoc.createElement('style');
          style.textContent = `
            .pdf-capture { background: #fff !important; }
            .pdf-capture .diagram-box { overflow: visible !important; height: auto !important; min-height: 400px; }
            .pdf-capture .zoom-controls { display: none !important; }
            .pdf-capture .diagram-hint { display: none !important; }
            .pdf-capture .pdf-exclude,
            .pdf-capture [data-pdf-exclude="true"] { display: none !important; }
            .pdf-capture * { transition: none !important; }
          `;
          clonedDoc.head.appendChild(style);
          clonedEl.classList.add('pdf-capture');
          // Replace Mermaid SVGs with img (data URL) so html2canvas renders them correctly
          clonedEl.querySelectorAll('.mermaid svg').forEach((svg) => {
            try {
              const svgString = new XMLSerializer().serializeToString(svg);
              const dataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
              const img = clonedDoc.createElement('img');
              img.src = dataUrl;
              img.alt = 'Diagram';
              img.setAttribute('style', 'max-width:100%; height:auto; display:block;');
              if (svg.parentNode) svg.parentNode.replaceChild(img, svg);
            } catch (_) {
              // keep SVG if conversion fails
            }
          });
        },
      });

      el.style.width = originalWidth;
      el.style.maxWidth = originalMaxWidth;

      const imgFormat = 'JPEG';
      const jpegQuality = 0.92;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginTop = 20;
      const marginBottom = 28;
      const marginSide = 20;
      const contentWidth = pageWidth - marginSide * 2;
      const contentHeight = pageHeight - marginTop - marginBottom;
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = contentWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      const sliceHeightPx = (contentHeight / scaledHeight) * imgHeight;

      // Map DOM break points to canvas coordinates
      const breakPointsCanvas = uniquePx.map((y) => Math.round(y * captureScale));

      let sourceY = 0;
      let isFirstPage = true;
      while (sourceY < imgHeight) {
        if (!isFirstPage) pdf.addPage();
        // Prefer to end the page at a block boundary (no cut-through words/elements)
        const idealEnd = sourceY + sliceHeightPx;
        const candidates = breakPointsCanvas.filter((bp) => bp > sourceY && bp <= idealEnd);
        const sliceEnd = candidates.length > 0
          ? Math.min(Math.max(...candidates), imgHeight)
          : Math.min(idealEnd, imgHeight);
        const thisSlicePx = Math.ceil(sliceEnd - sourceY);
        if (thisSlicePx <= 0) break;
        const thisSliceMm = (thisSlicePx / imgHeight) * scaledHeight;
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = imgWidth;
        sliceCanvas.height = thisSlicePx;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, sourceY, imgWidth, thisSlicePx, 0, 0, imgWidth, thisSlicePx);
        const sliceData = sliceCanvas.toDataURL('image/jpeg', jpegQuality);
        pdf.addImage(sliceData, imgFormat, marginSide, marginTop, contentWidth, thisSliceMm);
        sourceY = sliceEnd;
        isFirstPage = false;
      }
      const safeTitle = (post.title || 'blog-post').replace(/[^a-z0-9-_]/gi, '-').toLowerCase();
      pdf.save(`${safeTitle}.pdf`);
    } catch (err) {
      el.style.width = originalWidth;
      el.style.maxWidth = originalMaxWidth;
      console.error('PDF generation failed:', err);
      setMessage?.('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (!post) return <div className="text-center p-10 text-gray-500">Loading latest journal entry...</div>;

  const dateLine = formatDateLine(post);
  const changelog = getChangelog(post);
  const livingDoc = isLivingDoc(post);

  return (
    <article className="animate-fade-in">
      <div className="space-y-2">
        <BlogIntro isAdmin={isAdmin} setMessage={setMessage} />

        {isAdmin && (
          <EditPostForm
            post={post}
            isAdmin={isAdmin}
            setMessage={setMessage}
            onPostUpdated={onPostUpdated}
          />
        )}
      </div>

      <div className="panel mt-6 px-5 py-6 sm:px-8 sm:py-8">
        <div
          className="flex items-start justify-between gap-4 pb-5 mb-5 border-b border-line latest-entry-bar"
          data-pdf-exclude="true"
        >
          <div className="min-w-0 flex-1">
            {isLatestPost && (
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Latest entry</p>
            )}
            {!isLatestPost && onShowLatestPost && (
              <button
                type="button"
                onClick={onShowLatestPost}
                className="text-xs font-medium text-gray-500 hover:text-blue-600 uppercase tracking-wide transition-colors"
              >
                ← Back to latest entry
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            title="Download as PDF"
          >
            <Download className="w-4 h-4" />
            {isGeneratingPdf ? 'Generating…' : 'Download PDF'}
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-10 items-start mt-2">
          <div className="lg:col-span-2">
            <div ref={pdfContentRef} className="pdf-article-capture">
              <div className="flex flex-col mt-2">
                <div className="mb-1">
                  <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 leading-tight">
                    {post.title}
                  </h2>
                </div>
                {(livingDoc || post.tags?.length > 0) && (
                  <div className="flex flex-wrap items-center gap-2 mb-3 mt-3">
                    {livingDoc && (
                      <span className="px-2.5 py-1 text-xs font-medium bg-indigo-50 text-indigo-800 rounded-full ring-1 ring-indigo-200">
                        Living doc — updated as I learn more
                      </span>
                    )}
                    {post.tags?.map(tag => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 text-xs font-medium bg-stone-100 text-stone-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
                  <Calendar className="w-4 h-4 text-stone-600 shrink-0" />
                  <span>
                    {dateLine.single ? (
                      <>Published {dateLine.single}</>
                    ) : (
                      <>
                        Published {dateLine.published}
                        <span className="mx-1.5 text-gray-300">·</span>
                        Updated {dateLine.updated}
                      </>
                    )}
                  </span>
                </div>
              </div>

              {((post.image_urls && post.image_urls.length > 0) || post.image_url) && (
                <div className="mb-6 space-y-4">
                  {(post.image_urls && post.image_urls.length > 0 ? post.image_urls : [post.image_url])
                    .filter(url => url && url.trim())
                    .map((url, index) => (
                      <div key={index} className="w-full">
                        <img
                          src={url}
                          alt={`${post.title} - Image ${index + 1}`}
                          className="w-full rounded-lg shadow-lg object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                </div>
              )}

              {post.summary && !post.summary.includes('<') && (
                <p className="text-lg font-semibold text-gray-700 italic mb-4">{post.summary}</p>
              )}

              <div
                ref={contentRef}
                className="prose prose-lg max-w-none text-gray-800 leading-relaxed blog-content"
                dangerouslySetInnerHTML={{
                  __html: prepareBlogHtml(post.content)
                }}
              />

              {livingDoc && changelog.length > 0 && (
                <section className="mt-8 pt-6 border-t border-line">
                  <h3 className="font-serif text-xl font-semibold tracking-tight text-stone-900 mb-4">Changelog</h3>
                  <ul className="space-y-3">
                    {changelog.map((entry, index) => (
                      <li key={`${entry.date}-${index}`} className="flex gap-3 text-sm">
                        <time className="text-gray-500 shrink-0 w-28">
                          {entry.date
                            ? new Date(entry.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '—'}
                        </time>
                        <span className="text-gray-700">{entry.note}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            <CommentSection
              latestPost={post}
              userId={userId}
              isAdmin={isAdmin}
              setMessage={setMessage}
              onPostAdded={onPostUpdated}
            />
          </div>

          {sidebar && (
            <div className="lg:col-span-1 journal-sidebar pdf-exclude" data-pdf-exclude="true">
              {sidebar}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default LatestPostDisplay;
